import { call, put, select } from 'redux-saga/effects'
import { callApi } from '@/shared/tools/apiSagaHelper'
import {
  selectActiveTabId,
  selectActiveTabType,
  selectCharacteristicsCurrentContractActive,
  selectConfig,
  selectContractDetailsCurrentContractActive,
  selectCurrentContractActive,
  selectPreferencesContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { selectAuthData, selectExtraRoutesAuthData } from '../../../Login/AuthSelectors'

import { showSnackbar, SNACKBAR_TYPES } from '@/shared/tools/notifications/notification'
import {
  addContractErrors,
  addRecentBanking,
  updateClientData,
  updateContractConfigData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { generateParameters } from '@/shared/tools/contractSagaHelper'
import { selectAddressCurrentContractActive } from '../SubscriptionFormSelectors'
import { resetContractErrors } from '../SubscriptionFormSlice'
import * as Sentry from '@sentry/react'
import { t } from 'i18next'

export const commonActions = {
  CONTRACT_SAVE: 'CONTRACT_SAVE',
  RESEND_PROVIDER_EMAIL: 'RESEND_PROVIDER_EMAIL',
}

const saveSnackbar = ({ action, isIncomplete, checksBeforeSend, message }) => {
  if (action === 'save_final' && isIncomplete) {
    return { type: SNACKBAR_TYPES.WARNING, message: t('subscriptionForm:header.modal_save_send.saved_incomplete') }
  }
  if (checksBeforeSend && isIncomplete) {
    return { type: SNACKBAR_TYPES.ERROR, message: t('subscriptionForm:header.modal_save_send.missing_info') }
  }
  return { type: SNACKBAR_TYPES.SUCCESS, message: message ?? 'Unexpected save response message' }
}

const providerRiskFrom = response =>
  response?.provider_warning
    ? {
        level: response.provider_risk_level ?? null,
        message: response.provider_warning,
        sendHeld: response.send_held === true,
      }
    : null

export function* saveContract({ enqueue, action, closeModal, openModal }) {
  const typeRaw = yield select(selectActiveTabType)
  const type = typeRaw?.toLowerCase()
  const { contactId } = yield select(selectConfig)
  const {
    addressId: contractAddressId,
    billingAddressId: contractBillingAddressId,
    residenceAddressId: contractResidenceAddressId,
    elec = {},
    gas = {},
    propane = {},
    billing = {},
    consumption = {},
  } = yield select(selectCurrentContractActive)
  const contractId = yield select(selectActiveTabId)
  const { saveContractUrl } = yield select(selectExtraRoutesAuthData)
  const contractData = yield select(selectContractDetailsCurrentContractActive)
  const characteristicsData = yield select(selectCharacteristicsCurrentContractActive)
  const contactPreferences = yield select(selectPreferencesContactSubscriptionData)
  const contractAddressData = yield select(selectAddressCurrentContractActive)
  const { country } = yield select(selectAuthData)

  let formParameters

  try {
    formParameters = yield call(generateParameters, {
      type,
      contractId,
      contactId,
      addressId: contractAddressId,
      billingAddressId: contractBillingAddressId,
      residenceAddressId: contractResidenceAddressId,
      contractData,
      characteristicsData,
      contactPreferences,
      action,
      contractAddressData,
      electricityMeterData: elec ?? {},
      gasMeterData: gas ?? {},
      propaneData: propane ?? {},
      billingData: billing ?? {},
      energyConsumptionData: consumption ?? {},
      country,
    })
  } catch (err) {
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: err.message || `Unknown type: ${type}`,
    })
    return
  }

  if (!formParameters || typeof formParameters.entries !== 'function') {
    yield call(showSnackbar, enqueue, {
      type: SNACKBAR_TYPES.ERROR,
      message: `Couldn't generate parameters for type: ${type}`,
    })
    return
  }

  const formParametersObject = Object.fromEntries(formParameters.entries())
  const checksBeforeSend = typeof openModal === 'function'
  if (checksBeforeSend) {
    delete formParametersObject.create_reviews_interaction
  }

  yield put(
    updateContractConfigData({
      id: contractId,
      data: {
        isSaving: true,
      },
    }),
  )
  yield put(resetContractErrors({ id: contractId }))

  try {
    const result = yield call(callApi, {
      endpoint: saveContractUrl,
      enqueue,
      showErrorSnackbar: true,
      payload: formParametersObject,
      method: 'POST',
    })
    const sendHeld = result.send_held === true
    const isIncomplete = Object.keys(result.status_info || {}).length > 0
    const isSuccessAndComplete = action === 'save_final' && !sendHeld && !isIncomplete
    yield put(
      updateContractConfigData({
        id: contractId,
        data: {
          isSaving: false,
          completeStatusInfo: result.status_info || null,
          contractLastSaveValidate: isSuccessAndComplete ? new Date().toISOString() : null,
          providerRisk: providerRiskFrom(result),
        },
      }),
    )
    yield call(showSnackbar, enqueue, saveSnackbar({ action, isIncomplete, checksBeforeSend, message: result.message }))

    if (action === 'save_final' && typeof closeModal === 'function') {
      closeModal(false)
    }

    if (checksBeforeSend && !isIncomplete) {
      openModal(true)
    }

    if (billing.iban) {
      yield put(addRecentBanking({ iban: billing.iban, bic: billing.bic }))
    }

    if (formParametersObject.create_reviews_interaction === '1') {
      yield put(
        updateClientData({
          data: { isReviewRegistered: true, createReviewsInteraction: null, reviewLanguage: null },
          name: 'preferences',
        }),
      )
    }
  } catch (error) {
    const responseData = error?.responseData
    const riskChecked = responseData !== null && typeof responseData === 'object' && 'send_held' in responseData
    yield put(
      updateContractConfigData({
        id: contractId,
        data: {
          isSaving: false,
          ...(riskChecked ? { providerRisk: providerRiskFrom(responseData) } : {}),
        },
      }),
    )
    yield put(addContractErrors({ id: contractId, data: error.validationErrors }))
    if (typeof closeModal === 'function') {
      closeModal(false)
    }
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(error, { extra: { context: 'saveContractSaga' } })
    }
  }
}

export function* resendProviderEmail({ payload, callbackError, callbackSuccess }) {
  const contractId = yield select(selectActiveTabId)
  const { resendProviderEmailUrl } = yield select(selectExtraRoutesAuthData)

  try {
    const url = resendProviderEmailUrl?.replace('*', contractId)
    yield call(callApi, {
      endpoint: url,
      payload,
      method: 'POST',
    })
    if (callbackSuccess && typeof callbackSuccess === 'function') {
      callbackSuccess()
    }
  } catch (error) {
    if (callbackError && typeof callbackError === 'function') {
      callbackError(error)
    }
    const status = error?.status || 'unknown'
    if (import.meta.env.PROD && status !== 422) {
      Sentry.captureException(error, { extra: { context: 'resendProviderEmailSaga' } })
    }
  }
}
